/**
 * Ticket-date variant availability.
 *
 * Tour/ticket products (e.g. "De Bonte Koe Backstage Tours") get their "Date" variant
 * option generated by a separate ticketing app — snippets/product-variant-picker.liquid
 * just renders whatever date/time values that app has created as a plain <select>. The app
 * doesn't remove past sessions or block last-minute bookings on its own, so this file does
 * it client-side once the page has rendered:
 *   - past date/time options are hidden from the dropdown
 *   - options starting less than 24h from now are disabled (visible, but not selectable)
 *
 * Scoped by the option's handleized slug ("date"), not by product handle, so this applies
 * automatically to any current or future product using the same "Date" option convention —
 * see the `data-option-slug` attribute set in product-variant-picker.liquid.
 *
 * IMPORTANT: past-date options are hidden (`hidden` + `disabled`), never removed from the
 * DOM. VariantSelects.setAvailability() (assets/global.js) re-syncs every option's `disabled`
 * state after each variant change by looking each one up via `querySelector('option[value=
 * "..."]').disabled = false` for every value still present in the full variant list (which
 * still includes past dates — the ticketing app doesn't know about "past" either). If that
 * lookup doesn't find the option (because it was removed), setting `.disabled` on `null`
 * throws, which silently aborts the rest of that re-enabling pass and left every later date
 * — including valid future ones — stuck disabled. Hiding instead of removing keeps the
 * lookup working, and the reassertion below keeps our own hidden/disabled state from being
 * undone by that same resync.
 */
(function () {
  'use strict';

  var MONTH_NAMES = ['january','february','march','april','may','june','july','august','september','october','november','december'];
  var TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

  // The ticketing app's option values look like "June 26, 2026 2:00 PM" — every evergreen
  // browser's `new Date()` accepts that de facto format, but it isn't part of the ECMAScript
  // spec, so this regex reconstruction is a fallback for the rare engine that rejects it.
  // Returns null (rather than guessing) for anything that isn't confidently parseable, so an
  // unrecognized value is just left alone instead of risking hiding a real, valid date.
  function parseTicketDate(rawValue) {
    if (!rawValue) return null;

    var native = new Date(rawValue);
    if (!isNaN(native.getTime())) return native;

    var match = String(rawValue).trim().match(/^([A-Za-z]+)\s+(\d{1,2}),\s*(\d{4})\s+(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) return null;
    var monthIndex = MONTH_NAMES.indexOf(match[1].toLowerCase());
    if (monthIndex === -1) return null;

    var hour24 = parseInt(match[4], 10) % 12;
    if (match[6].toUpperCase() === 'PM') hour24 += 12;
    var parsed = new Date(parseInt(match[3], 10), monthIndex, parseInt(match[2], 10), hour24, parseInt(match[5], 10));
    return isNaN(parsed.getTime()) ? null : parsed;
  }

  // Hides past-date options, disables anything inside the next 24h, and — if the option
  // that was actually selected became unavailable — moves the selection to the next
  // available date and fires a real 'change' event so the variant-selects custom element
  // (assets/global.js) resyncs price/availability/add-to-cart exactly as if a visitor had
  // picked that date themselves.
  function pruneDateSelect(selectEl) {
    var now = new Date();
    var disableBefore = new Date(now.getTime() + TWENTY_FOUR_HOURS_MS);
    var selectedOptionBecameUnavailable = false;

    Array.prototype.slice.call(selectEl.options).forEach(function (optionEl) {
      var optionDate = parseTicketDate(optionEl.value);
      if (!optionDate) return; // not a date option (or unparseable) — leave untouched

      if (optionDate < now) {
        optionEl.hidden = true;
        optionEl.disabled = true;
        if (optionEl.selected) selectedOptionBecameUnavailable = true;
      } else if (optionDate < disableBefore) {
        optionEl.disabled = true;
        if (optionEl.selected) selectedOptionBecameUnavailable = true;
      }
    });

    if (selectedOptionBecameUnavailable) {
      var nextAvailable = Array.prototype.slice.call(selectEl.options).find(function (optionEl) {
        return !optionEl.hidden && !optionEl.disabled;
      });
      if (nextAvailable) {
        selectEl.value = nextAvailable.value;
        selectEl.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  }

  function pruneDateSelectsIn(root) {
    root.querySelectorAll('.variant-input-wrapper[data-option-slug="date"] select').forEach(function (selectEl) {
      pruneDateSelect(selectEl);

      // VariantSelects.setAvailability() (assets/global.js) re-enables every option value
      // still present in the product's full variant list on every subsequent variant change
      // (e.g. picking a different Ticket type) — including past/soon-disabled dates, since
      // the ticketing app's variant data has no concept of "past" or "too soon". Registering
      // on the ancestor <variant-selects> (after its own constructor already has) guarantees
      // this listener runs after that resync, so it can immediately reassert our state.
      if (selectEl.dataset.dateAvailabilityBound) return;
      selectEl.dataset.dateAvailabilityBound = 'true';
      var variantSelects = selectEl.closest('variant-selects');
      if (variantSelects) {
        variantSelects.addEventListener('change', function () { pruneDateSelect(selectEl); });
      }
    });
  }

  function init() {
    pruneDateSelectsIn(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Quick-view drawer content is fetched and injected into the page after the initial load
  // (see quick-view.js), so a "Date" dropdown opened that way needs its own pass.
  document.addEventListener('quickview:loaded', init);
})();
